import type { BeforeRequestHook } from "ky";
import ky, { isHTTPError } from "ky";
import pLimit from "p-limit";
import { API_KEY } from "../../constants/index.ts";
import { isTokenExpiringSoon, shouldAuthenticate } from "./auth-helpers.ts";
import { getCurrentApp } from "./get-current-app.ts";
import { generateRequestId, logBeforeError } from "./hooks/index.ts";
import { redirectToErrorPage, redirectToLogin } from "./redirect-to-login.ts";

type GetToken = () => Promise<string>;

const limiter = pLimit(1);

let sessionExpiresAt: number | null = null;

export function getSessionExpiresAt() {
  return sessionExpiresAt;
}

function isSessionExpired(): boolean {
  return sessionExpiresAt !== null && Date.now() / 1000 > sessionExpiresAt;
}

// `JSON.parse("")` throws, and ky calls `parseJson` for empty bodies too
// (e.g. HTTP 204 No Content). Read an empty body as `null`. Without this, a
// successful 204 fails its React Query query with a SyntaxError.
const parseJson = (text: string): unknown =>
  text === "" ? null : JSON.parse(text);

export const createPublicClient = () =>
  ky.extend({
    prefix: "/",
    // Let react-query handle retries rather than ky for now
    // We may want to change this if we have ky handle token refreshes
    retry: 0,
    parseJson,
    hooks: {
      beforeRequest: [setAppHeader, generateRequestId],
      beforeError: [logBeforeError],
    },
  });

export const createPrivateClient = ({
  getToken = readOrGetAuthToken,
}: {
  getToken?: GetToken;
} = {}) => {
  return ky.extend({
    prefix: "/",
    // Let react-query handle retries rather than ky for now
    // We may want to change this if we have ky handle token refreshes
    retry: 0,
    parseJson,
    hooks: {
      beforeRequest: [setAuthorizationHeader(getToken), setAppHeader],
      beforeError: [logBeforeError],
    },
  });
};

const setAuthorizationHeader =
  (getToken: GetToken): BeforeRequestHook =>
  async ({ request }) => {
    if (shouldAuthenticate(request)) {
      // If we know the session has expired, redirect immediately without
      // making a network request.
      if (isSessionExpired()) {
        redirectToErrorPage();
        throw new Error("Session expired");
      }

      try {
        const sessionToken = await limiter(getToken);

        request.headers.set("Authorization", `Bearer ${sessionToken}`);
      } catch (error: unknown) {
        if (isHTTPError(error) && error.response.status === 401) {
          redirectToLogin();
          throw error;
        }

        throw error;
      }
    }
  };

const setAppHeader: BeforeRequestHook = ({ request }) => {
  const app = getCurrentApp();

  if (app) {
    request.headers.set("App", app);
  }
};

export const privateClient = createPrivateClient();
export const publicClient = createPublicClient();

async function readOrGetAuthToken(): Promise<string> {
  let sessionToken = sessionStorage.getItem(API_KEY);

  if (!sessionToken || isTokenExpiringSoon(sessionToken)) {
    const response = await ky("/api/auth/tokens", {
      credentials: "same-origin",
      headers: {
        App: getCurrentApp() ?? "",
      },
      hooks: {
        beforeError: [logBeforeError],
      },
      prefix: "/",
    });

    const expiresAtHeader = response.headers.get("x-session-expires-at");
    if (expiresAtHeader) {
      const parsed = Number(expiresAtHeader);
      sessionExpiresAt = Number.isFinite(parsed) ? parsed : null;
    }

    sessionToken = await response.text();
    sessionStorage.setItem(API_KEY, sessionToken);
  }

  return sessionToken;
}
